/**
 * Unescapes HTML entities in a string
 * @param {string} text - The text to unescape
 * @returns {string} - The unescaped text
 */
export function unescapeHtmlEntities(text) {
  const entities = {
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&',
    '&quot;': '"',
    '&#39;': "'",
  };

  return text.replace(/&lt;|&gt;|&amp;|&quot;|&#39;/g, match => {
    return entities[match];
  });
}

/**
 * Sanitizes a URL by removing invalid characters that can cause markdown serialization issues
 * @param {string} url - The URL to sanitize
 * @returns {string} - The sanitized URL
 */
export function sanitizeUrl(url) {
  if (!url) return url;

  // Remove trailing backslashes and periods
  // Example: "https://example.com\" -> "https://example.com"
  // Example: "https://example.com." -> "https://example.com"
  url = url.replace(/[\\.]+$/, '');

  // Remove all backslashes throughout the URL (backslashes are not valid in URLs)
  // Example: "https://example.com\path" -> "https://example.compath"
  url = url.replace(/\\/g, '');

  // Trim leading and trailing whitespace
  url = url.trim();

  return url;
}

/**
 * Converts plain URLs in text to markdown links
 * Handles URLs with http://, https://, query parameters, and fragments
 * Skips URLs that are already in markdown link format
 * @param {string} content - The content to process
 * @returns {string} - The content with plain URLs converted to markdown links
 *
 * @example
 * // Plain URL
 * convertPlainUrlsToMarkdown('Visit https://example.com')
 * // Returns: 'Visit [https://example.com](https://example.com)'
 *
 * @example
 * // Already markdown - no change
 * convertPlainUrlsToMarkdown('Visit [Google](https://google.com)')
 * // Returns: 'Visit [Google](https://google.com)'
 *
 * @example
 * // URL with query params and fragment
 * convertPlainUrlsToMarkdown('Check https://example.com?foo=bar#section')
 * // Returns: 'Check [https://example.com?foo=bar#section](https://example.com?foo=bar#section)'
 */
export function convertPlainUrlsToMarkdown(content) {
  if (!content) return content;

  // Store markdown links temporarily to avoid processing URLs inside them
  const markdownLinks = [];
  const PLACEHOLDER_PREFIX = '___MARKDOWN_LINK_';

  // Step 1: Find and replace existing markdown links with placeholders
  // Matches: [any text](any url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let processedContent = content.replace(markdownLinkRegex, match => {
    const placeholder = `${PLACEHOLDER_PREFIX}${markdownLinks.length}___`;
    markdownLinks.push(match);
    return placeholder;
  });

  // Step 2: Convert plain URLs to markdown format
  // Matches: http:// or https:// followed by non-whitespace characters
  // Supports query parameters (?...), fragments (#...), paths, etc.
  // Stops at whitespace, or common sentence-ending punctuation when followed by space/newline
  const urlRegex = /https?:\/\/[^\s]+/g;

  processedContent = processedContent.replace(urlRegex, url => {
    // Remove trailing punctuation if it's likely sentence punctuation
    // (period, comma, exclamation, question mark at the very end)
    let cleanUrl = url;
    const trailingPunctuation = url.match(/[.,!?]+$/);
    if (trailingPunctuation) {
      cleanUrl = url.slice(0, -trailingPunctuation[0].length);
    }

    // Convert to markdown link format
    const markdownUrl = `[${cleanUrl}](${cleanUrl})`;

    // If we removed punctuation, add it back after the markdown link
    return trailingPunctuation
      ? markdownUrl + trailingPunctuation[0]
      : markdownUrl;
  });

  // Step 3: Restore original markdown links
  markdownLinks.forEach((originalLink, index) => {
    const placeholder = `${PLACEHOLDER_PREFIX}${index}___`;
    processedContent = processedContent.replace(placeholder, originalLink);
  });

  return processedContent;
}
